import React, { useEffect, useMemo, useRef, useState } from "react"
import { Download, File, Loader2 } from "lucide-react"

import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { getLocalStringDateTime } from "@/utils/date"
import { formatMoney } from "@/utils/utils"

import { DetailedWalletTransaction, WALLET_TRANSACTION_TYPE_LABELS, WalletTransaction } from "@/types/TransactionsType"
import { getGatewayConfig, getPaymentMethodConfig, getStatusConfig } from "@/utils/transactions/utils"
import Tag from "components/ui/tag"
import { http } from "@/utils/http"
import { DOWNLOAD_TRANSACTION_PROOF_URL, GET_TRANSACTION_DETAIL_URL } from "@/constants/api/endpoints"
import { Button } from "components/ui/button"
import { Separator } from "@/components/ui/separator"

interface TransactionDetailSheetProps {
  transactionId: string
  sheetOpen: boolean
  onSheetOpenChange: (state: boolean) => void
  onValidate?: (() => void) | null
  onReject?: (() => void) | null
}

// ── Small helper to keep rows DRY ─────────────────────────────────────────────
function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <p className="text-sm text-[#225C6B]">{label}</p>
      <div className="text-black font-semibold text-sm">{children}</div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

function TransactionDetailSheet({
                                  transactionId,
                                  sheetOpen = false,
                                  onSheetOpenChange,
                                  onValidate = null,
                                  onReject = null,
                                }: TransactionDetailSheetProps) {
  const [selectedTransaction, setSelectedTransaction] = useState<DetailedWalletTransaction | null>(null)
  const [isDownloadingProof, setIsDownloadingProof] = useState(false)
  const hasFetched = useRef(false)

  const configStatus = useMemo(
    () => getStatusConfig(selectedTransaction?.status!),
    [selectedTransaction],
  )
  const configPaymentMethod = useMemo(
    () => getPaymentMethodConfig(selectedTransaction?.payment_method!),
    [selectedTransaction],
  )
  const configGateway = useMemo(
    () => getGatewayConfig(selectedTransaction?.gateway!),
    [selectedTransaction],
  )

  useEffect(() => {
    if (!transactionId || hasFetched.current) return
    hasFetched.current = true

    http
      .get<WalletTransaction[]>(GET_TRANSACTION_DETAIL_URL.replace(":id", transactionId))
      .then((res) => setSelectedTransaction(res.data))
      .catch((e) => {
        if (e.name !== "CanceledError") console.error(e)
      })
  }, [transactionId])

  const handleDownloadProof = async () => {
    if (!selectedTransaction?.id) return
    setIsDownloadingProof(true)
    try {
      const response = await http.get(DOWNLOAD_TRANSACTION_PROOF_URL.replace(":id", selectedTransaction.id))
      const { url } = response.data
      window.open(url, "_blank")
    } catch (e) {
      console.error("Erreur lors du téléchargement :", e)
    } finally {
      setIsDownloadingProof(false)
    }
  }

  // ─────────────────────────────────────────────────────────────────────────

  const tx = selectedTransaction

  return (
    <Sheet open={sheetOpen} onOpenChange={onSheetOpenChange}>
      <SheetContent className="sm:max-w-2xl flex flex-col">
        <SheetHeader>
          <SheetTitle>Détails de la transaction</SheetTitle>
        </SheetHeader>

        {tx && (
          <>
            {/* ── Scrollable body ── */}
            <SheetDescription asChild>
              <div className="flex-1 overflow-y-auto space-y-8 pt-6 pr-1">

                {/* Section 1 — Identité */}
                <div className="grid grid-cols-3 gap-6">
                  <DetailRow label="Date de création">
                    {getLocalStringDateTime(tx.created_at)}
                  </DetailRow>
                  <DetailRow label="État">
                    <Tag className="py-1.5 px-2 rounded-md" type={configStatus.color}>
                      {configStatus.label}
                      {["PENDING", "VALIDATED"].includes(tx.status!) ? " (Demandé)" : ""}
                    </Tag>
                  </DetailRow>
                  <DetailRow label="Référence">
                    {tx.reference}
                  </DetailRow>
                </div>

                {/* Section 2 — Type, direction, montant */}
                <div className="grid grid-cols-3 gap-6">
                  <DetailRow label="Type de transaction">
                    {WALLET_TRANSACTION_TYPE_LABELS[tx.transaction_type]}
                  </DetailRow>
                  <DetailRow label="Sens">
                    <Tag
                      className="py-1.5 px-2 rounded-md"
                      type={tx.direction === "CREDIT" ? "success" : "danger"}
                    >
                      {tx.direction === "CREDIT" ? "Crédit" : "Débit"}
                    </Tag>
                  </DetailRow>
                  <DetailRow label="Montant">
                    <span className="text-base font-bold">{formatMoney(parseFloat(tx.amount))} F CFA</span>
                  </DetailRow>
                </div>

                {/* Section 3 — Paiement & passerelle */}
                <div className="grid grid-cols-3 gap-6">
                  <DetailRow label="Moyen de paiement">
                    {tx.payment_method
                      ? <Tag className="rounded-lg px-4 py-2"
                             type={configPaymentMethod.color}>{configPaymentMethod.label}</Tag>
                      : <span className="text-gray-400 italic font-normal">—</span>
                    }
                  </DetailRow>
                  <DetailRow label="Passerelle">
                    {configGateway.label || <span className="text-gray-400 italic font-normal">—</span>}
                  </DetailRow>
                  <DetailRow label="ID passerelle">
                    <span className="text-xs break-all">
                      {tx.gateway_transaction_id ?? <span className="text-gray-400 italic font-normal">—</span>}
                    </span>
                  </DetailRow>
                </div>

                {/* Section 4 — Initiateur + validateur + facture */}
                <div className="grid grid-cols-3 gap-6">
                  <DetailRow label="Auteur">
                    <div>
                      <p className="font-semibold text-[#173E48]">{tx.initiator_name}</p>
                      <p className="text-xs text-[#225C6B] font-normal">{tx.initiator_email}</p>
                    </div>
                  </DetailRow>

                  {(tx.validator_name || tx.validator_email) && (
                    <DetailRow label="Validé par">
                      <div>
                        <p className="font-semibold text-[#173E48]">{tx.validator_name}</p>
                        <p className="text-xs text-[#225C6B] font-normal">{tx.validator_email}</p>
                      </div>
                    </DetailRow>
                  )}

                  {tx.invoice_reference && (
                    <DetailRow label="Référence facture">
                      {tx.invoice_reference}
                    </DetailRow>
                  )}
                </div>

                {/* Section 5 — Dates de suivi */}
                {(tx.completed_at || tx.validated_at) && (
                  <div className="grid grid-cols-3 gap-6">
                    {tx.completed_at && (
                      <DetailRow label="Date de complétion">
                        {getLocalStringDateTime(tx.completed_at)}
                      </DetailRow>
                    )}
                    {tx.validated_at && (
                      <DetailRow label="Date de validation">
                        {getLocalStringDateTime(tx.validated_at)}
                      </DetailRow>
                    )}
                  </div>
                )}

                {/* Section 6 — Justificatif */}
                {tx.has_proof_file && (
                  <div className="rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="bg-purple-100 text-purple-600 p-3 border border-purple-500 rounded-lg">
                          <File size={28} />
                        </span>
                        <p className="font-medium text-gray-900">Fichier justificatif virement</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={isDownloadingProof}
                        onClick={handleDownloadProof}
                      >
                        {isDownloadingProof ? (
                          <>
                            <Loader2 size={16} className="mr-2 animate-spin" />
                            Téléchargement...
                          </>
                        ) : (
                          <>
                            <Download size={16} className="mr-2" />
                            Télécharger le justificatif
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Section 7 — Motif & Note */}
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-semibold text-[#225C6B] mb-1">Motif</p>
                    <p className="text-sm text-gray-600">{tx.motif ?? "Pas de motif renseigné."}</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#225C6B] mb-1">Note</p>
                    <p className="text-sm text-gray-600">{tx.notes ?? "Aucune note disponible."}</p>
                  </div>
                </div>

              </div>
            </SheetDescription>

            {/* ── Sticky footer — only rendered when needs_validation is true and at least one handler is provided ── */}
            {tx.needs_validation && (onValidate || onReject) && (
              <>
                <Separator className="mt-4" />
                <SheetFooter className="pt-3">
                  <div className="flex justify-between w-full">
                    {onReject && (
                      <Button
                        variant="destructive"
                        onClick={() => {
                          onReject()
                          onSheetOpenChange(false)
                        }}
                      >
                        Rejeter
                      </Button>
                    )}
                    {onValidate && (
                      <Button
                        onClick={() => {
                          onValidate()
                          onSheetOpenChange(false)
                        }}
                      >
                        Valider la transaction
                      </Button>
                    )}
                  </div>
                </SheetFooter>
              </>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}

export default TransactionDetailSheet